import { useState } from "react";
import type { BlobWithFilename } from "../utils/download";
import { createProgressToast } from "../utils/progress-toast";

interface DownloadHooks {
  onProgress?: (progress: number) => void;
}

interface DownloadOptions {
  fileName?: string;
  fileType?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  showToasts?: boolean;
  progressMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  onCompleteTracking?: (result: { file_name: string; file_size_bytes: number; duration_ms: number }) => void;
  onFailedTracking?: (error: { error_message: string; duration_ms: number }) => void;
}

interface DownloadState {
  isDownloading: boolean;
  error: Error | null;
}

/**
 * Download function receives optional hooks from useFileDownload.
 * Call `hooks.onProgress?.(percent)` to update the progress toast.
 */
type DownloadFunction = (hooks?: DownloadHooks) => Promise<Blob | BlobWithFilename | undefined>;

export const useFileDownload = () => {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    isDownloading: false,
    error: null
  });

  const downloadFile = async (downloadFn: DownloadFunction, options: DownloadOptions = {}): Promise<void> => {
    const {
      fileName = "adora-images",
      fileType,
      onSuccess,
      onError,
      showToasts = false,
      progressMessage = "Downloading...",
      successMessage = "Download completed successfully",
      errorMessage = "Download failed. Please try again.",
      onCompleteTracking,
      onFailedTracking
    } = options;

    let hasReceivedProgress = false;
    const progressToast = showToasts ? createProgressToast(progressMessage, { indeterminate: true }) : null;
    const downloadStartedAt = Date.now();

    const handleProgress = (progress: number) => {
      if (!hasReceivedProgress && progressToast) {
        // Switch from indeterminate to determinate on first progress event
        hasReceivedProgress = true;
      }
      progressToast?.update(progress);
    };

    try {
      setDownloadState({ isDownloading: true, error: null });

      const result = await downloadFn({ onProgress: handleProgress });

      if (!result) {
        throw new Error("Download failed: no data received");
      }

      // Handle both Blob and BlobWithFilename return types
      let blob: Blob;
      let serverFilename: string | null = null;

      if (result instanceof Blob) {
        blob = result;
      } else {
        blob = result.blob;
        serverFilename = result.filename;
      }

      // Use server filename if available (already includes extension)
      // Otherwise fall back to client-provided filename
      let downloadFilename: string;

      if (serverFilename) {
        downloadFilename = serverFilename;
      } else {
        let finalFileName = fileName;
        let finalFileType = fileType;

        if (!finalFileType) {
          const lastDotIndex = fileName.lastIndexOf(".");
          if (lastDotIndex !== -1 && lastDotIndex < fileName.length - 1) {
            finalFileName = fileName.slice(0, Math.max(0, lastDotIndex));
            finalFileType = fileName.slice(Math.max(0, lastDotIndex + 1));
          } else {
            finalFileType = "zip";
          }
        }
        downloadFilename = `${finalFileName}.${finalFileType}`;
      }

      const fileURL = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = downloadFilename;
      link.click();
      window.URL.revokeObjectURL(fileURL);

      onCompleteTracking?.({
        file_name: downloadFilename,
        file_size_bytes: blob.size,
        duration_ms: Date.now() - downloadStartedAt
      });

      setDownloadState({ isDownloading: false, error: null });

      if (progressToast) {
        progressToast.success(successMessage);
      }

      onSuccess?.();
    } catch (error_) {
      const error = error_ instanceof Error ? error_ : new Error("Download failed");

      onFailedTracking?.({
        error_message: error.message,
        duration_ms: Date.now() - downloadStartedAt
      });

      setDownloadState({ isDownloading: false, error });

      if (progressToast) {
        progressToast.error(errorMessage);
      }

      onError?.(error);
    }
  };

  return {
    downloadFile,
    isDownloading: downloadState.isDownloading,
    error: downloadState.error
  } as const;
};
